import { resetFieldMeta } from '@neo/blocks/forms/form-util'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { cn } from '@neo/lib/utils'
import { Alert } from '@neo/primitives/alert'
import { Typography } from '@neo/primitives/typography'
import type { ApiResponse } from '@shared/types'
import type { UseMutateFunction } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useCallback, useMemo, useState } from 'react'
import { queryClient } from 'v2/configs/rq-setup'
import {
  Currency,
  GstStatus,
  getGstEndIcon,
  getPostalCodeLabel,
  getZipCodeEndIcon,
  gstValidationSchema,
  type ValidationStatusType,
  validateGstWithRegex,
  zipCodeValidationSchema,
} from 'v2/pages/billing/billing.config'
import { useIsApiConsole } from 'v2/shared/hooks/use-api-route'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { billingQueryKeys } from 'v2/shared/utils/query-key-management'
import { nameSchema } from 'v2/shared/validation-schemas'
import { getLocalAuth } from 'v2/utils'
import { errorToast, successToast } from 'v2/utils/toasts'
import z from 'zod'
import { useUpdateOrgNameMutation } from '../../mutations'
import {
  useAccountDetailsQuery,
  useBillingAddressQuery,
  useGstValidationQuery,
  useZipCodeValidationQuery,
} from '../../queries'
import type { BillingInformationForm, RUpdateBillingInformation } from '../../types'
import { countryOptions, getAccountId, getStateOptions, getZipCodeStatus } from '../../utils'

interface BillingInformationProps {
  onNext?: () => void
  variant?: 'modal' | 'page'
  updateBillingInformation: UseMutateFunction<
    RUpdateBillingInformation,
    Error,
    BillingInformationForm,
    unknown
  >
  isUpdatingBillingInformation: boolean
}

export const BillingInformation = ({
  onNext,
  variant = 'page',
  updateBillingInformation,
  isUpdatingBillingInformation,
}: BillingInformationProps) => {
  const { data: billingInformation } = useBillingAddressQuery()
  const { data: accountDetails } = useAccountDetailsQuery()
  const { mutateAsync: updateOrgName, isPending: isUpdatingOrgName } = useUpdateOrgNameMutation()
  const { email } = getLocalAuth()
  const isIndiaCurrency = accountDetails?.account.currency === Currency.INR
  const [gstNumber, setGstNumber] = useState(billingInformation?.gst_number || '')
  const [zipCode, setZipCode] = useState(billingInformation?.zipcode || '')
  const [selectedCountry, setSelectedCountry] = useState(
    billingInformation?.country_id || (isIndiaCurrency ? 'IN' : 'US'),
  )
  const [selectedState, setSelectedState] = useState(billingInformation?.state || '')
  const isApiConsole = useIsApiConsole()
  const { data: gstValidation, isLoading: isGstValidating } = useGstValidationQuery(gstNumber)
  const { data: zipCodeValidation, isLoading: isZipCodeValidating } = useZipCodeValidationQuery(
    zipCode,
    selectedCountry,
    selectedState,
  )

  const handleGstChange = useCallback((state: BillingInformationForm) => {
    const value = state.gst_number || ''
    setGstNumber(value)
  }, [])

  const handleZipCodeChange = useCallback((state: BillingInformationForm) => {
    const value = state.zipcode || ''
    setZipCode(value)
  }, [])

  const handleCountryChange = useCallback((state: BillingInformationForm) => {
    const value = state.country_id || ''
    setSelectedCountry(value)
  }, [])

  const handleStateChange = useCallback((state: BillingInformationForm) => {
    const value = state.state || ''
    setSelectedState(value)
  }, [])

  const getGstStatus = useCallback((): ValidationStatusType => {
    if (!gstNumber) return 'idle'
    if (isGstValidating) return 'loading'
    if (gstNumber.length === 15) {
      if (!validateGstWithRegex(gstNumber)) return 'invalid'
      return gstValidation?.isValid ? 'valid' : 'invalid'
    }
    return gstNumber.length > 0 ? 'invalid' : 'idle'
  }, [gstNumber, gstValidation?.isValid, isGstValidating])

  const getGstErrorMessage = useCallback((): string | undefined => {
    if (!gstNumber || gstNumber.length !== 15) return undefined
    if (!validateGstWithRegex(gstNumber)) return undefined
    if (isGstValidating) return undefined
    if (gstValidation?.gstinStatus && gstValidation.gstinStatus !== GstStatus.ACTIVE) {
      return `GST status: ${gstValidation.gstinStatus}`
    }
    return undefined
  }, [gstNumber, gstValidation, isGstValidating])

  const defaultValue = useMemo<BillingInformationForm>(
    () => ({
      name: billingInformation?.name || '',
      org_name: getLocalAuth()?.organization_name || '',
      address_line_1: billingInformation?.address_line_1 || '',
      city: billingInformation?.city || '',
      country_id: billingInformation?.country_id || (isIndiaCurrency ? 'IN' : 'US'),
      state: billingInformation?.state || '',
      zipcode: billingInformation?.zipcode || '',
      gst_number: billingInformation?.gst_number || '',
      gst_registered: billingInformation?.gst_registered || false,
      actor: email || '',
      client: 'CX',
      remarks: 'Updating Billing Address',
    }),
    [billingInformation, email, isIndiaCurrency],
  )

  const getBillingInformationFormFields = (componentVariant: 'modal' | 'page') => {
    const fields: GenericFieldConfig<BillingInformationForm> = [
      {
        name: 'org_name',
        label: 'Organization Name',
        componentType: 'input',
        type: 'sub-item',
        zodValidation: nameSchema('Organization name', 140, true),
        inputProps: {
          placeholder: 'Enter organization name',
          disabled: isUpdatingOrgName,
          'data-testid': 'input-org-name',
        },
      },
      {
        name: 'separator',
        type: 'slot',
        component: <hr className="mt-2 col-span-full" />,
      },
      {
        name: 'alert',
        type: 'slot',
        component:
          componentVariant === 'modal' ? (
            <Alert
              variant="warning"
              title="Billing address not found"
              content="Billing address is required before adding your first card."
              className="col-span-2 border-none bg-warning text-warning-foreground"
            />
          ) : null,
      },
      {
        name: 'address',
        type: 'slot',
        component: <h1 className="text-h4-500">Address</h1>,
      },
      {
        name: 'name',
        label: componentVariant === 'modal' ? 'Company Name' : 'Name',
        componentType: 'input',
        zodValidation: nameSchema(componentVariant === 'modal' ? 'Company name' : 'Name', 50, true),
        inputProps: {
          placeholder: componentVariant === 'modal' ? 'Enter company name' : 'Enter name',
          disabled: isUpdatingBillingInformation,
          maxLength: 50,
          'data-testid':
            componentVariant === 'modal' ? 'billing-company-name-input' : 'billing-name-input',
        },
      },
      {
        name: 'address_line_1',
        label: 'Address',
        componentType: 'input',
        zodValidation: z
          .string()
          .min(1, 'Address is required')
          .max(128, 'Address cannot exceed 128 characters'),
        inputProps: {
          placeholder: 'Enter address',
          disabled: isUpdatingBillingInformation,
          maxLength: 128,
        },
      },
      {
        name: 'city',
        label: 'City',
        className: 'col-span-1',
        componentType: 'input',
        zodValidation: z
          .string()
          .min(1, 'City is required')
          .max(128, 'City cannot exceed 128 characters'),
        inputProps: {
          placeholder: 'Enter city',
          disabled: isUpdatingBillingInformation,
          maxLength: 128,
        },
      },
      {
        name: 'state',
        label: 'State',
        className: 'col-span-1',
        componentType: getStateOptions(selectedCountry).length > 0 ? 'select' : 'input',
        onChangeListenTo: ['country_id'],
        zodValidation: z
          .string()
          .min(1, 'State is required')
          .max(32, 'State cannot exceed 32 characters'),
        afterOnChange: handleStateChange,
        getDynamicProps: (_, __, form) => {
          const currentCountry = form?.state.values.country_id || selectedCountry
          return {
            inputProps:
              getStateOptions(currentCountry).length > 0
                ? {
                    options: getStateOptions(currentCountry),
                    placeholder: 'Select state',
                    disabled: isUpdatingBillingInformation,
                  }
                : {
                    placeholder: 'Enter state',
                    disabled: isUpdatingBillingInformation,
                    maxLength: 32,
                  },
          }
        },
      },
      {
        name: 'zipcode',
        className: 'col-span-1',
        componentType: 'input',
        onChangeListenTo: ['country_id', 'state'],
        afterOnChange: handleZipCodeChange,
        getDynamicProps: (_, __, form) => {
          const currentCountry = form?.state.values.country_id || selectedCountry
          const isIndia = currentCountry === 'IN'
          const isSixDigits = zipCode.length === 6
          const apiError =
            zipCodeValidation?.status === false ? zipCodeValidation.errorMsg : undefined
          return {
            zodVal: zipCodeValidationSchema(currentCountry),
            inputProps: {
              label: getPostalCodeLabel(currentCountry),
              placeholder: `Enter ${getPostalCodeLabel(currentCountry)}`,
              disabled: isUpdatingBillingInformation,
              maxLength: 10,
              endIcon: getZipCodeEndIcon(
                getZipCodeStatus(zipCode, currentCountry, zipCodeValidation, isZipCodeValidating),
              ),
              'data-testid': 'zipcode-input',
              endIconClassName: 'pr-2',
              error: isIndia && isSixDigits ? apiError : undefined,
            },
          }
        },
      },
      {
        name: 'country_id',
        label: 'Country',
        className: 'col-span-1',
        componentType: 'select',
        zodValidation: z.string().min(1, 'Country is required'),
        afterOnChange: (state, _, form) => {
          handleCountryChange(state)
          form?.setFieldValue('address_line_1', '')
          form?.setFieldValue('city', '')
          form?.setFieldValue('state', '')
          form?.setFieldValue('zipcode', '')
          if (form) {
            resetFieldMeta(form, 'address_line_1')
            resetFieldMeta(form, 'city')
            resetFieldMeta(form, 'state')
            resetFieldMeta(form, 'zipcode')
          }
          setSelectedState('')
          setZipCode('')
        },
        inputProps: {
          options: countryOptions(),
          disabled: isUpdatingBillingInformation || isIndiaCurrency,
        },
      },
      {
        name: 'hr',
        type: 'slot',
        component: <hr className="mt-2 col-span-full" />,
      },
      {
        name: 'section',
        type: 'slot',
        component: (
          <div className="col-span-full ">
            <Typography variant="H4/500">GST Details</Typography>
            <span className="text-p-400 text-secondary">
              Let us know your GST registration status for proper invoicing and tax calculation. If
              you&apos;re registered, please enter your valid GST number.
            </span>
          </div>
        ),
      },
      {
        name: 'gst_registered',
        label: 'GST Status',
        componentType: 'radioGroup',
        zodValidation: z.boolean(),
        inputProps: {
          options: [
            { label: 'Unregistered', value: false },
            { label: 'Registered', value: true },
          ],
          disabled: isUpdatingBillingInformation,
        },
        visible: !!isIndiaCurrency,
      },
      {
        name: 'gst_number',
        onChangeListenTo: ['gst_registered'],
        visible: (v) => isIndiaCurrency && !!v.gst_registered,
        componentType: 'input',
        zodValidation: gstValidationSchema,
        afterOnChange: handleGstChange,
        getDynamicProps: () => ({
          inputProps: {
            placeholder: 'Enter GST number',
            disabled: isUpdatingBillingInformation,
            maxLength: 20,
            endIcon: getGstEndIcon(getGstStatus()),
            endIconClassName: 'pr-2',
            error: getGstErrorMessage(),
            'data-testid': 'gst-number-input',
          },
        }),
        className: '-mt-2',
      },
    ]
    const filteredFields = fields.filter((field) => {
      if (
        !isIndiaCurrency &&
        ['hr', 'section', 'gst_registered', 'gst_number'].includes(field.name)
      )
        return false
      if (variant === 'modal' && (field.name === 'hr' || field.name === 'section')) return false
      if (variant === 'modal' && ['org_name', 'organization', 'separator'].includes(field.name))
        return false
      if (variant === 'modal' && field.name === 'billing-section') return false
      if (isApiConsole && ['org_name', 'separator'].includes(field.name)) return false
      return true
    })

    if (variant === 'modal') {
      const gstFields = filteredFields.filter(
        (field) => field.name === 'gst_registered' || field.name === 'gst_number',
      )
      const otherFields = filteredFields.filter(
        (field) => field.name !== 'gst_registered' && field.name !== 'gst_number',
      )
      const nameFieldIndex = otherFields.findIndex((field) => field.name === 'name')
      return [
        ...otherFields.slice(0, nameFieldIndex),
        ...gstFields,
        ...otherFields.slice(nameFieldIndex),
      ]
    }

    return filteredFields
  }

  return (
    <GenericForm<BillingInformationForm>
      id="billing-information"
      className={cn('grid-cols-2', variant === 'modal' ? '' : 'w-[500px]', 'gap-4')}
      submitText={isUpdatingBillingInformation || isUpdatingOrgName ? 'Updating...' : 'Update'}
      isSubmitting={isUpdatingBillingInformation || isUpdatingOrgName}
      includeSubmitButton={variant === 'page'}
      fields={getBillingInformationFormFields(variant)}
      defaultValues={defaultValue}
      onSubmit={async (e) => {
        const { gst_registered, gst_number, ...rest } = e
        const payload = isIndiaCurrency ? { ...rest, gst_registered, gst_number } : { ...rest }

        try {
          // Only validate if GST is registered
          if ('gst_registered' in payload && payload.gst_registered) {
            const gstStatus = getGstStatus()

            // Block submission if GST validation is in progress or invalid
            // Error is shown inline with the input field
            if (gstStatus === 'loading' || gstStatus !== 'valid') {
              return
            }
          }

          // Validate zipcode - block submission if invalid or still validating
          const zipCodeStatus = getZipCodeStatus(
            zipCode,
            selectedCountry,
            zipCodeValidation,
            isZipCodeValidating,
          )

          const isIndiaCountry = selectedCountry === 'IN'
          if (zipCodeStatus === 'loading') {
            errorToast({
              title: 'Validation In Progress',
              description: `Please wait for ${isIndiaCountry ? 'pincode' : 'zipcode'} validation to complete`,
            })
            return
          }

          if (zipCodeStatus === 'invalid') {
            errorToast({
              title: `${isIndiaCountry ? 'Pincode' : 'Zipcode'} Validation Error`,
              description: `Please enter a valid ${isIndiaCountry ? 'pincode' : 'zipcode'}`,
            })
            return
          }

          // For India, require successful API validation
          if (selectedCountry === 'IN' && zipCode.length === 6 && zipCodeStatus !== 'valid') {
            errorToast({
              title: 'Pincode Validation Required',
              description: 'Please enter a valid pincode for the selected state',
            })
            return
          }

          // Update org name if changed
          const currentOrgName = getLocalAuth()?.organization_name || ''
          if (e.org_name && e.org_name !== currentOrgName) {
            try {
              await updateOrgName({ name: e.org_name })
              successToast({
                title: 'Success',
                description: 'Organization name updated successfully',
              })
            } catch (orgErr) {
              const orgErrorText = getErrorText(orgErr as AxiosError<ApiResponse<unknown>>)
              errorToast({
                title: 'Error',
                description: orgErrorText || 'Failed to update organization name',
              })
              return
            }
          }

          updateBillingInformation(payload, {
            onSuccess: async () => {
              successToast({
                title: 'Success',
                description: 'Billing information updated successfully',
              })
              await queryClient.invalidateQueries({
                queryKey: billingQueryKeys().address(getAccountId()),
              })
              onNext?.()
            },
            onError: (error) => {
              const errorText = getErrorText(error as AxiosError<ApiResponse<unknown>>)
              errorToast({
                title: 'Error',
                description: errorText || 'Failed to update billing information',
              })
            },
          })
        } catch (err) {
          if (err instanceof Error) {
            errorToast({
              title: 'Error',
              description: err.message,
            })
          } else {
            errorToast({
              title: 'Error',
              description: 'Something went wrong. Please try again.',
            })
          }
        }
      }}
    />
  )
}
